import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive, unwrapReactive } from '@jack-henry/frontend-utils/lit';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { ResearchTransactionFilterModelDto } from '@treasury/api/channel';
import { AccountsService, ResearchedTransaction } from '@treasury/domain/accounts';
import { CheckImage } from '@treasury/domain/accounts/types/check-image.entity';
import { CompanyAccount } from '@treasury/domain/company/company-account.entity';
import { TmApiError } from '@treasury/domain/shared';
import '@treasury/omega/components/omega-button';
import { TmContainer } from '@treasury/presentation';
import { TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-card';
import {
    type CurrencyRange,
    CurrencyRangeType,
} from '@treasury/presentation/components/tm-currency-range';
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-multi-select';
import { type TextRange, TextRangeType } from '@treasury/presentation/components/tm-text-range';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-text-range';
import {
    SideDrawerButtonPosture,
    TmSideDrawerService,
} from '@treasury/presentation/services/side-drawer';
import '@vaadin/grid';
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import './check-lookup/check-lookup-table';

const defaultAmountRange: CurrencyRange = {
    rangeType: CurrencyRangeType.AllAmounts,
};

const defaultDateRange: DateRange = {
    rangeType: DateRangeType.Today,
};

const defaultTextRange: TextRange = {
    rangeType: TextRangeType.All,
};

const defaultFilters = {
    accounts: [],
    dateRange: defaultDateRange,
    checkNumber: defaultTextRange,
    amountRange: defaultAmountRange,
};

const tagName = 'check-lookup-widget';
@customElement(tagName)
export class CheckLookupWidget extends TmContainer {
    @InjectProperty()
    declare private readonly accountsService: AccountsService;

    @InjectProperty()
    declare private readonly drawerService: TmSideDrawerService;

    @DeepReactive()
    private filters: {
        accounts: CompanyAccount[];
        dateRange: DateRange;
        checkNumber: TextRange;
        amountRange: CurrencyRange;
    } = defaultFilters;

    @state()
    private accounts: CompanyAccount[] = [];

    @state()
    private applyFiltersDisabled = false;

    @state()
    private transactions: ResearchedTransaction[] = [];

    @state()
    private openedTransactions: ResearchedTransaction[] = [];

    @state()
    private checkImages: CheckImage[] = [];

    @state()
    private depositCheckImage?: CheckImage;

    @state()
    private loadingCheckImages = false;

    @state()
    private loadingDepositImages = false;

    @state()
    private transactionBeingFetched?: ResearchedTransaction;

    @state()
    private depositCheckOpened: CheckImage[] = [];

    protected async firstUpdated() {
        this.filters = defaultFilters;
        this.loading = true;
        try {
            this.accounts = await this.accountsService.getAllAccounts();
            this.filters.accounts = [...this.accounts];
        } catch (e: unknown) {
            this.notificationService.renderError(e as TmApiError);
        } finally {
            this.loading = false;
        }
    }

    protected render() {
        return html`<tm-card .header=${'Check Lookup'}
            >${this.renderCheckLookupSearchForm()}</tm-card
        >`;
    }

    protected renderCheckLookupSearchForm() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        return html`<div>
            <div id="check-lookup-search-form">
                <tm-multi-select
                    required
                    label="Accounts"
                    .items=${this.accounts}
                    .allowSelectAll=${true}
                    .allSelectedText=${'All Accounts'}
                    .initial=${[...this.accounts]}
                    .mapItem=${(a: CompanyAccount) => ({
                        value: a,
                        label: a.accountDisplayLabel ?? a.name,
                    })}
                    @value-changed=${(e: TmValueChanged<CompanyAccount[]>) => {
                        this.filters.accounts = e.detail.value || [];
                        this.applyFiltersDisabled = this.filters.accounts.length < 1;
                    }}
                ></tm-multi-select>
                <tm-date-range
                    label="Date"
                    class="mb-3"
                    .initial=${this.filters.dateRange}
                    @value-changed=${(e: TmValueChanged<DateRange>) => {
                        const { value } = e.detail;
                        if (value) {
                            this.filters.dateRange = value;
                        }
                    }}
                ></tm-date-range>
                <tm-text-range
                    label="Check number(s)"
                    .range=${this.filters.checkNumber}
                    @selection=${(e: CustomEvent) => {
                        this.filters.checkNumber = e.detail;
                    }}
                ></tm-text-range>
                <tm-currency-range
                    label="Amount"
                    .initial=${this.filters.amountRange}
                    @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                        const { value } = e.detail;
                        if (!value) {
                            return;
                        }
                        this.filters.amountRange = value;
                    }}
                ></tm-currency-range>
            </div>
            <div class="flex w-full items-center justify-center pt-4">
                <tm-button
                    class="mr-2"
                    importance="primary"
                    .disabled=${this.applyFiltersDisabled}
                    @click=${() => {
                        this.fetchRecords();
                    }}
                    >Lookup</tm-button
                >
                <tm-button
                    importance="secondary"
                    @click=${() => {
                        this.resetFilters();
                    }}
                    >Reset</tm-button
                >
            </div>
        </div>`;
    }

    private resetFilters() {
        this.filters = defaultFilters;
        this.applyFiltersDisabled = true;
        this.transactions = [];
        this.drawerService.close();
    }

    private async fetchRecords() {
        const filters = mapCheckLookupFilters(unwrapReactive(this.filters));
        this.loading = true;
        try {
            const response = await this.accountsService.researchTransactions(
                filters as unknown as ResearchTransactionFilterModelDto
            );
            this.transactions = filterResultsToCheckLookup(response);
            this.openDrawer();
        } catch (e: unknown) {
            this.notificationService.renderError(e as TmApiError);
        } finally {
            this.loading = false;
        }
    }

    private openDrawer() {
        this.drawerService.open(
            html`<check-lookup-table .transactions=${this.transactions}></check-lookup-table>`,
            'Check Lookup',
            {
                buttons: [
                    {
                        label: 'New lookup',
                        reason: DynamicComponentExitReason.Cancel,
                        posture: SideDrawerButtonPosture.Primary,
                    },
                ],
                footerAlignment: 'left',
            }
        );
    }

    public static get styles() {
        return [
            css`
                :host {
                    --tm-card-min-width: auto;
                    --tm-card-min-height: 250px;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CheckLookupWidget;
    }
}

export const filterResultsToCheckLookup = (filterResults: ResearchedTransaction[]) =>
    filterResults.filter((trans: ResearchedTransaction) => trans.checkNumber);

const mapDateRange = (dateRange: DateRange) => {
    const { rangeType, startDate, endDate } = dateRange;
    if (rangeType === 'Today') {
        return {
            transactionDateOperator: 'Today',
            transactionDateFrom: new Date().toLocaleDateString('en-US'),
            transactionDateTo: new Date().toLocaleDateString('en-US'),
        };
    } else {
        return {
            transactionDateOperator: rangeType,
            transactionDateFrom: startDate?.toLocaleDateString('en-US'),
            transactionDateTo: endDate
                ? endDate.toLocaleDateString('en-US')
                : startDate?.toLocaleDateString('en-US'),
        };
    }
};
export const mapCheckRange = (checkRange: TextRange) => {
    const { rangeType, specificText, floorText, ceilText } = checkRange;
    return {
        referenceNumberOperator: rangeType,
        referenceNumberFrom: specificText || floorText || undefined,
        referenceNumberTo: ceilText ? ceilText : undefined,
    };
};
export const mapAmountRange = (amountRange: CurrencyRange) => {
    const { rangeType, specificAmount, floorAmount, ceilAmount } = amountRange;
    return {
        amountOperator: rangeType,
        amountFrom: specificAmount || (floorAmount ? floorAmount : undefined),
        amountTo: ceilAmount ? ceilAmount : undefined,
    };
};
export const mapCheckLookupFilters = (filters: {
    accounts: CompanyAccount[];
    dateRange: DateRange;
    checkNumber: TextRange;
    amountRange: CurrencyRange;
}) => {
    return {
        accounts: filters.accounts.map(account => ({ ...account.toDto() })),
        ...mapDateRange(filters.dateRange),
        ...mapCheckRange(filters.checkNumber),
        ...mapAmountRange(filters.amountRange),
        paymentDirection: 'Both',
        accountTypes: [{ name: 'Checking' }, { name: 'Savings' }],
        maxRecordCount: 501,
    };
};
